import * as XLSX from 'xlsx';

/**
 * Export filtered transactions to Excel
 * @param {Array} transactions - Array of transaction objects
 * @param {string} type - Transaction type (deposit, payout, withdrawal)
 * @param {Object} filters - Applied filters for filename
 */
export const exportTransactionsToExcel = (transactions, type, filters = {}) => {
    try {
        if (!transactions || transactions.length === 0) {
            alert('No transactions to export');
            return;
        }

        // Define columns based on transaction type and schema
        const getDepositColumns = () => [
            { key: 'createdAt', label: 'Date Created' },
            { key: 'transactionId', label: 'Transaction ID' },
            { key: 'amount', label: 'Expected Amount' },
            { key: 'actualBalance', label: 'Actual Balance' },
            { key: 'fee', label: 'Fee' },
            { key: 'netAmount', label: 'Net Amount' },
            { key: 'currencyType', label: 'Currency' },
            { key: 'walletAddress', label: 'Wallet Address' },
            { key: 'status', label: 'Status' },
            { key: 'confirmedAt', label: 'Confirmed At' },
            { key: 'expiresAt', label: 'Expires At' },
            { key: 'invoiceNo', label: 'Invoice No' },
            { key: 'paymentStatus', label: 'Payment Status' },
            { key: 'blockchainTxHash', label: 'Blockchain Hash' },
            // { key: 'clientReference', label: 'Client Reference' },
        ];

        const getWithdrawalColumns = () => [
            { key: 'createdAt', label: 'Date Created' },
            { key: 'amount', label: 'Amount' },
            { key: 'fee', label: 'Fee' },
            { key: 'netAmount', label: 'Net Amount' },
            { key: 'currencyType', label: 'Currency' },
            { key: 'clientWallet', label: 'Client Wallet' },
            { key: 'status', label: 'Status' },
            // { key: 'type', label: 'Type' },
            { key: 'processedAt', label: 'Processed At' },
            { key: 'adminNotes', label: 'Admin Notes' },
            { key: 'transactionHash', label: 'Transaction Hash' },
            { key: 'blockchainTxHash', label: 'Blockchain Hash' },
            { key: 'invoiceNo', label: 'Invoice No' },
            // { key: 'clientReference', label: 'Client Reference' },
            { key: 'blockchainNetwork', label: 'Network' },
            { key: 'blockchainStatus', label: 'Blockchain Status' }
        ];

        // Select appropriate columns based on type
        let columns;
        switch (type) {
            case 'deposit':
                columns = getDepositColumns();
                break;
            case 'payout':
            case 'withdrawal':
                columns = getWithdrawalColumns();
                break;
            default:
                // Fallback to deposit columns for unknown types
                columns = getWithdrawalColumns();
                break;
        }

        // Format transaction data for Excel based on schema
        const formattedData = transactions.map(transaction => {
            const row = {};

            columns.forEach(column => {
                let value = transaction[column.key];

                // Handle blockchain data nested fields
                if (column.key === 'blockchainNetwork' && transaction.blockchainData) {
                    value = transaction.blockchainData.network || '';
                } else if (column.key === 'blockchainStatus' && transaction.blockchainData) {
                    value = transaction.blockchainData.status ? 'Success' : 'Failed';
                }

                // Handle verification data for deposits
                else if (column.key === 'actualBalance' && type === 'deposit') {
                    value = transaction.actualBalance || transaction.verificationData?.actualBalance || '';
                }

                // Format specific fields
                if (column.key === 'createdAt' || column.key === 'confirmedAt' || column.key === 'processedAt' || column.key === 'expiresAt') {
                    value = value ? new Date(value).toLocaleString() : '';
                } else if (column.key === 'amount' || column.key === 'actualBalance' || column.key === 'fee' || column.key === 'netAmount') {
                    value = value ? parseFloat(value).toFixed(2) : '0.00';
                } else if (column.key === 'status') {
                    value = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                } else if (column.key === 'type') {
                    value = value ? value.charAt(0).toUpperCase() + value.slice(1) :
                        (transaction.transactionId ? 'Deposit' : 'Withdrawal');
                } else if (column.key === 'paymentStatus') {
                    value = value || 'N/A';
                } else if (column.key === 'currencyType') {
                    value = value || 'USDT-TRC20';
                }

                row[column.label] = value || '';
            });

            return row;
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);

        // Auto-size columns
        const colWidths = columns.map(col => {
            const maxLength = Math.max(
                col.label.length,
                ...formattedData.map(row => String(row[col.label] || '').length)
            );
            return { width: Math.min(Math.max(maxLength + 2, 10), 50) };
        });
        worksheet['!cols'] = colWidths;

        // Add worksheet to workbook
        const sheetName = `${type.charAt(0).toUpperCase() + type.slice(1)} Transactions`;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate filename with transaction type, filters and timestamp
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/[:.]/g, '-');

        let filename = `merchant_${type}_transactions_${timestamp}`;

        // Add filter info to filename
        if (filters.status && filters.status !== 'all') {
            filename += `_status_${filters.status}`;
        }
        if (filters.startDate && filters.endDate) {
            filename += `_${filters.startDate}_to_${filters.endDate}`;
        } else if (filters.startDate) {
            filename += `_from_${filters.startDate}`;
        } else if (filters.endDate) {
            filename += `_until_${filters.endDate}`;
        }

        filename += '.xlsx';

        // Export file
        XLSX.writeFile(workbook, filename);

        return {
            success: true,
            message: `${transactions.length} ${type} transactions exported successfully to ${filename}`,
            filename,
            transactionType: type,
            recordCount: transactions.length
        };

    } catch (error) {
        console.error('Excel export error:', error);
        return {
            success: false,
            message: 'Failed to export transactions to Excel',
            error: error.message
        };
    }
};

/**
 * Export all three transaction types to separate sheets in one Excel file
 * @param {Object} transactionData - Object containing deposits, payouts, withdrawals arrays
 * @param {Object} filters - Applied filters
 */
export const exportAllTransactionsToExcel = (transactionData, filters = {}) => {
    try {
        const { deposits = [], payouts = [], withdrawals = [] } = transactionData;

        if (deposits.length === 0 && payouts.length === 0 && withdrawals.length === 0) {
            alert('No transactions to export');
            return;
        }

        const workbook = XLSX.utils.book_new();

        // Helper function to format data for a specific transaction type using schema-based columns
        const formatTransactionData = (transactions, type) => {
            // Define columns based on transaction type and schema
            const getDepositColumns = () => [
                { key: 'createdAt', label: 'Date Created' },
                { key: 'transactionId', label: 'Transaction ID' },
                { key: 'amount', label: 'Expected Amount' },
                { key: 'actualBalance', label: 'Actual Balance' },
                { key: 'fee', label: 'Fee' },
                { key: 'netAmount', label: 'Net Amount' },
                { key: 'currencyType', label: 'Currency' },
                { key: 'walletAddress', label: 'Wallet Address' },
                { key: 'status', label: 'Status' },
                { key: 'confirmedAt', label: 'Confirmed At' },
                { key: 'expiresAt', label: 'Expires At' },
                { key: 'invoiceNo', label: 'Invoice No' },
                { key: 'paymentStatus', label: 'Payment Status' },
                { key: 'blockchainTxHash', label: 'Blockchain Hash' },
                // { key: 'clientReference', label: 'Client Reference' }
            ];

            const getWithdrawalColumns = () => [
                { key: 'createdAt', label: 'Date Created' },
                { key: 'amount', label: 'Amount' },
                { key: 'fee', label: 'Fee' },
                { key: 'netAmount', label: 'Net Amount' },
                { key: 'currencyType', label: 'Currency' },
                { key: 'clientWallet', label: 'Client Wallet' },
                { key: 'status', label: 'Status' },
                // { key: 'type', label: 'Type' },
                { key: 'processedAt', label: 'Processed At' },
                { key: 'adminNotes', label: 'Admin Notes' },
                { key: 'transactionHash', label: 'Transaction Hash' },
                { key: 'blockchainTxHash', label: 'Blockchain Hash' },
                { key: 'invoiceNo', label: 'Invoice No' },
                // { key: 'clientReference', label: 'Client Reference' },
                { key: 'blockchainNetwork', label: 'Network' },
                { key: 'blockchainStatus', label: 'Blockchain Status' }
            ];

            // Select appropriate columns based on type
            let columns;
            switch (type) {
                case 'deposit':
                    columns = getDepositColumns();
                    break;
                case 'payout':
                case 'withdrawal':
                    columns = getWithdrawalColumns();
                    break;
                default:
                    columns = getWithdrawalColumns();
                    break;
            }

            return transactions.map(transaction => {
                const row = {};

                columns.forEach(column => {
                    let value = transaction[column.key];

                    // Handle blockchain data nested fields
                    if (column.key === 'blockchainNetwork' && transaction.blockchainData) {
                        value = transaction.blockchainData.network || '';
                    } else if (column.key === 'blockchainStatus' && transaction.blockchainData) {
                        value = transaction.blockchainData.status ? 'Success' : 'Failed';
                    }

                    // Handle verification data for deposits
                    else if (column.key === 'actualBalance' && type === 'deposit') {
                        value = transaction.actualBalance || transaction.verificationData?.actualBalance || '';
                    }

                    // Format specific fields
                    if (column.key === 'createdAt' || column.key === 'confirmedAt' || column.key === 'processedAt' || column.key === 'expiresAt') {
                        value = value ? new Date(value).toLocaleString() : '';
                    } else if (column.key === 'amount' || column.key === 'actualBalance' || column.key === 'fee' || column.key === 'netAmount') {
                        value = value ? parseFloat(value).toFixed(2) : '0.00';
                    } else if (column.key === 'status') {
                        value = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                    } else if (column.key === 'type') {
                        value = value ? value.charAt(0).toUpperCase() + value.slice(1) :
                            (transaction.transactionId ? 'Deposit' : 'Withdrawal');
                    } else if (column.key === 'paymentStatus') {
                        value = value || 'N/A';
                    } else if (column.key === 'currencyType') {
                        value = value || 'USDT-TRC20';
                    }

                    row[column.label] = value || '';
                });

                return row;
            });
        };

        // Add deposits sheet
        if (deposits.length > 0) {
            const depositData = formatTransactionData(deposits, 'deposit');
            const depositSheet = XLSX.utils.json_to_sheet(depositData);
            XLSX.utils.book_append_sheet(workbook, depositSheet, 'Deposits');
        }

        // Add payouts sheet
        if (payouts.length > 0) {
            const payoutData = formatTransactionData(payouts, 'payout');
            const payoutSheet = XLSX.utils.json_to_sheet(payoutData);
            XLSX.utils.book_append_sheet(workbook, payoutSheet, 'Payouts');
        }

        // Add withdrawals sheet
        if (withdrawals.length > 0) {
            const withdrawalData = formatTransactionData(withdrawals, 'withdrawal');
            const withdrawalSheet = XLSX.utils.json_to_sheet(withdrawalData);
            XLSX.utils.book_append_sheet(workbook, withdrawalSheet, 'Withdrawals');
        }

        // Generate filename with better filter information
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/[:.]/g, '-');

        let filename = `merchant_all_transactions_${timestamp}`;

        // Add filter info to filename
        if (filters.status && filters.status !== 'all') {
            filename += `_status_${filters.status}`;
        }
        if (filters.startDate && filters.endDate) {
            filename += `_${filters.startDate}_to_${filters.endDate}`;
        } else if (filters.startDate) {
            filename += `_from_${filters.startDate}`;
        } else if (filters.endDate) {
            filename += `_until_${filters.endDate}`;
        }

        filename += '.xlsx';

        // Export file
        XLSX.writeFile(workbook, filename);

        const totalTransactions = deposits.length + payouts.length + withdrawals.length;

        return {
            success: true,
            message: `${totalTransactions} transactions exported successfully across ${workbook.SheetNames.length} sheets to ${filename}`,
            filename,
            recordCount: totalTransactions,
            sheetCount: workbook.SheetNames.length
        };

    } catch (error) {
        console.error('Excel export error:', error);
        return {
            success: false,
            message: 'Failed to export transactions to Excel',
            error: error.message
        };
    }
};
